import styled from "@emotion/styled";
import Modal from "react-modal";
import UI from "@mile-meeting/mile-ui";
import { getKorDate, getTimeString } from "utils/date";
import { useState } from "react";
import { FadeLoader } from "react-spinners";
import { TEmailNoShowRepeatReservation } from "../types";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    border: "none",
    background: "transparent",
    transform: "translate(-50%, -50%)",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};
Modal.setAppElement("#root");

type TRepeatCancelModal = {
  reservationInfo: TEmailNoShowRepeatReservation | null;
  onHandleCancel: () => Promise<void>;
  openModal: boolean;
  closeModal: () => void;
  type: "cancel" | "cancelRequest";
};

const RepeatCancelModal = (props: TRepeatCancelModal) => {
  const { reservationInfo, onHandleCancel, openModal, closeModal, type } = props;
  const [loading, setLoading] = useState<boolean>(false);

  const onHandleBtn = async () => {
    if (loading) return;
    setLoading(true);
    await onHandleCancel();
    setLoading(false);
  };

  const getTitle = () => {
    if (type === "cancel") {
      return (
        <p>
          “{reservationInfo?.title}” 반복 예약을
          <br /> 취소하시겠습니까?
        </p>
      );
    } else {
      return (
        <p>
          “{reservationInfo?.title}” 반복 예약의 취소를
          <br />
          요청하시겠습니까?
        </p>
      );
    }
  };

  const getSubTitle = () => {
    if (type === "cancel") {
      return (
        <p>
          취소 시, 반복 예약의 모든 일정이 취소되며
          <br />
          예약자/참석자에게 취소 알림이 전송됩니다.
        </p>
      );
    } else {
      return (
        <p>
          <div>취소 요청 시, 예약자에게 노쇼 반복 예약 취소를 요청하는 알림이 전송됩니다.</div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            ※ 예약자에게 전송되는 알림이 궁금하다면?
            <UI.ButtonGroups.Text
              title="알림 미리보기"
              size="small"
              onClick={() =>
                window.open(
                  "https://mile-corporation.notion.site/12ea222a9c1b817f8c76d7ed927e8352#13ba222a9c1b807dac2dfd30fa0b0b01"
                )
              }
              styles={{
                container: {
                  display: "inline-block",
                },
              }}
            />
          </div>
        </p>
      );
    }
  };

  const getBtnTitle = () => {
    if (type === "cancel") {
      return "반복 예약 취소하기";
    } else {
      return "반복 예약 취소 요청하기";
    }
  };

  if (!reservationInfo) return null;
  return (
    <Modal style={modalStyle} isOpen={openModal} onRequestClose={closeModal}>
      <Container>
        <CancelContainer>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelContainer>
        <UpperSection>
          <AlertIcon>
            <UI.Icon name="info" size={56} color="var(--alias-color-status-cautionary)" />
          </AlertIcon>
          <Content>
            <UI.Typography
              type="heading01"
              fontStyle="bold"
              color="var(--alias-color-label-normal)"
              style={{
                textAlign: "center",
              }}
            >
              {getTitle()}
            </UI.Typography>
            <UI.Typography
              type="body01 normal"
              fontStyle="medium"
              color="var(--alias-color-label-alternative)"
              style={{
                textAlign: "center",
              }}
            >
              {getSubTitle()}
            </UI.Typography>
            <Line />
            <UI.Typography
              type="body01 normal"
              fontStyle="regular"
              color="var(--alias-color-label-neutral)"
              style={{
                width: "100%",
              }}
            >
              <span style={{ fontWeight: 700 }}>[예약 제목]</span>
              <br />• 예약자: {reservationInfo.rsvCreator}
              <br />• 참석자: {reservationInfo.attendees.length < 1 ? "-" : reservationInfo.attendees.join(", ")}
              <br />
              {reservationInfo.recurrenceData &&
                `• 예약 반복: ${reservationInfo.recurrenceData.startAt ? getKorDate(reservationInfo?.recurrenceData.startAt).replaceAll(".", ". ") : "N/D"} - 
                    ${reservationInfo.recurrenceData.terminatedAt ? getKorDate(reservationInfo?.recurrenceData.terminatedAt).replaceAll(".", ". ") : "무기한 반복"} | 
                    ${reservationInfo?.recurrenceData.rruleStr}`}
              <br />
              {reservationInfo.recurrenceData &&
                `• 예약 시간: ${getTimeString(reservationInfo.recurrenceData.rsvStartTime ?? "")} - 
                    ${getTimeString(reservationInfo.recurrenceData.rsvEndTime ?? "")}`}
            </UI.Typography>
          </Content>
        </UpperSection>
        <UI.Button
          title={getBtnTitle()}
          size="large"
          style={{ width: "100%", gap: 0 }}
          onClick={onHandleBtn}
          suffixIcon={
            <div
              style={{
                transform: "scale(0.3)",
                display: "inline-flex",
                width: 16,
              }}
            >
              <FadeLoader color="white" height={16} radius={4} width={4} />
            </div>
          }
          showSuffixIcon={loading}
        />
      </Container>
    </Modal>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  width: 580px;
  padding: 48px;
  box-sizing: border-box;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;

  border-radius: 20px;
  background: var(--alias-color-background-normal-normal);
  box-shadow: var(---alias-style-shadow-heavy);
`;

const CancelContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
`;

const UpperSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`;

const AlertIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 40px 0px rgba(255, 201, 51, 0.42);
  border-radius: 50%;
  width: 46px;
  height: 46px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--alias-color-line-normal);
`;
export default RepeatCancelModal;
